import type { AiWorkflowSlug } from '@/lib/ai-workflows';

export type AiWorkflowTaskPresetFieldKey =
  | 'brief'
  | 'sourceText'
  | 'context'
  | 'audience'
  | 'tone'
  | 'outputFormat'
  | 'extraInstructions';

export type AiWorkflowTaskPresetValues = Partial<Record<AiWorkflowTaskPresetFieldKey, string>>;

export type AiWorkflowTaskPreset = {
  id: string;
  toolSlug: AiWorkflowSlug;
  title: string;
  description: string;
  recommendedFor: string;
  values: AiWorkflowTaskPresetValues;
  starterToken: string;
  tags: string[];
  featured?: boolean;
};

export const AI_WORKFLOW_TASK_STARTER_TOKEN =
  '[replace with your exact task, topic, product, audience shift, or goal]';

export const aiWorkflowTaskPresetFieldLabels: Record<AiWorkflowTaskPresetFieldKey, string> = {
  brief: 'Brief',
  sourceText: 'Source material',
  context: 'Context',
  audience: 'Audience',
  tone: 'Tone',
  outputFormat: 'Output format',
  extraInstructions: 'Extra instructions',
};

function createPreset(
  toolSlug: AiWorkflowSlug,
  input: Omit<AiWorkflowTaskPreset, 'toolSlug' | 'starterToken'>,
): AiWorkflowTaskPreset {
  return {
    ...input,
    toolSlug,
    starterToken: AI_WORKFLOW_TASK_STARTER_TOKEN,
  };
}

export const aiWorkflowTaskPresets: AiWorkflowTaskPreset[] = [
  createPreset('ai-story-generator', {
    id: 'short-story-from-prompt',
    title: 'Short story from a prompt',
    description: 'Turn a one-line premise into a complete short story with a clear arc.',
    recommendedFor: 'Anyone who wants a finished story fast from a simple idea.',
    featured: true,
    tags: ['story', 'fiction', 'short story'],
    values: {
      brief: `Write a complete short story based on this premise: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      tone: 'vivid, engaging',
      outputFormat: 'markdown',
      extraInstructions:
        'Give it a title, a clear beginning, middle, and end, one main character with a want and an obstacle, and a satisfying turn. Around 500-800 words.',
    },
  }),
  createPreset('ai-story-generator', {
    id: 'bedtime-story-for-kids',
    title: 'Bedtime story for kids',
    description: 'A gentle, age-appropriate bedtime story from a theme or characters.',
    recommendedFor: 'Parents who want a calm, original bedtime story.',
    tags: ['story', 'kids', 'bedtime'],
    values: {
      brief: `Write a gentle bedtime story about: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      audience: 'children ages 4-8',
      tone: 'warm, gentle, calming',
      outputFormat: 'markdown',
      extraInstructions:
        'Keep vocabulary simple, avoid scary themes, and end on a soft, reassuring note. Around 300-500 words.',
    },
  }),
  createPreset('ai-story-generator', {
    id: 'plot-outline-from-idea',
    title: 'Plot outline from an idea',
    description: 'Turn a concept into a structured plot outline you can write from.',
    recommendedFor: 'Writers who want structure before drafting.',
    tags: ['story', 'outline', 'planning'],
    values: {
      brief: `Create a plot outline for a story about: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      tone: 'clear, structural',
      outputFormat: 'markdown',
      extraInstructions:
        'Return a beat-by-beat outline: hook, setup, inciting incident, rising action, midpoint, climax, resolution. One or two lines per beat.',
    },
  }),
  createPreset('ai-paraphrasing-tool', {
    id: 'standard-paraphrase',
    title: 'Rephrase (standard)',
    description: 'Reword text clearly while keeping the exact meaning.',
    recommendedFor: 'Cleaning up phrasing without changing substance.',
    featured: true,
    tags: ['paraphrase', 'rewrite', 'clarity'],
    values: {
      brief:
        'Paraphrase the source text below. Keep the meaning, change the wording and structure.',
      sourceText: '[paste the text you want reworded here]',
      tone: 'clear, natural',
      outputFormat: 'plain text',
      extraInstructions: 'Do not add new information. Return only the rewritten text.',
    },
  }),
  createPreset('ai-paraphrasing-tool', {
    id: 'formal-paraphrase',
    title: 'Rephrase (formal / academic)',
    description: 'Rewrite text in a more formal, professional, or academic register.',
    recommendedFor: 'Students and professionals who need a formal tone.',
    tags: ['paraphrase', 'formal', 'academic'],
    values: {
      brief:
        'Paraphrase the source text below in a formal, academic register while keeping the meaning.',
      sourceText: '[paste the text you want reworded here]',
      tone: 'formal, precise',
      outputFormat: 'plain text',
      extraInstructions: 'Avoid slang and contractions. Do not add new claims.',
    },
  }),
  createPreset('ai-paraphrasing-tool', {
    id: 'simplify-paraphrase',
    title: 'Simplify (plain language)',
    description: 'Rewrite dense text so it is easy to read and understand.',
    recommendedFor: 'Making complex text accessible.',
    tags: ['paraphrase', 'simplify', 'plain language'],
    values: {
      brief: 'Rewrite the source text below in plain, simple language while keeping the meaning.',
      sourceText: '[paste the dense text here]',
      audience: 'a general reader',
      tone: 'simple, plainspoken',
      outputFormat: 'plain text',
      extraInstructions: 'Use short sentences and common words. Do not lose key facts.',
    },
  }),
  createPreset('ai-summarizer', {
    id: 'tldr-summary',
    title: 'TL;DR summary',
    description: 'Condense long text into a short, skimmable gist.',
    recommendedFor: 'Getting the point of a long article or document fast.',
    featured: true,
    tags: ['summary', 'tldr', 'skim'],
    values: {
      brief: 'Summarize the source text below into a short TL;DR plus 3-5 key bullet points.',
      sourceText: '[paste the long text, article, or notes here]',
      outputFormat: 'markdown',
      extraInstructions:
        'Lead with a one-line TL;DR, then key points as bullets. Summarize only what is in the source.',
    },
  }),
  createPreset('ai-summarizer', {
    id: 'executive-summary',
    title: 'Executive summary',
    description: 'A short, structured summary for a busy decision-maker.',
    recommendedFor: 'Reports and documents that need a professional brief.',
    tags: ['summary', 'executive', 'report'],
    values: {
      brief: 'Write an executive summary of the source text below.',
      sourceText: '[paste the report or document here]',
      audience: 'executives and decision-makers',
      tone: 'concise, professional',
      outputFormat: 'markdown',
      extraInstructions:
        'Include overview, key findings, and any implications or next steps that appear in the source. Do not invent data.',
    },
  }),
  createPreset('ai-summarizer', {
    id: 'key-takeaways',
    title: 'Key takeaways (bullets)',
    description: 'Pull the most important points out as a clean bullet list.',
    recommendedFor: 'Notes, transcripts, and study material.',
    tags: ['summary', 'bullets', 'notes'],
    values: {
      brief: 'Extract the key takeaways from the source text below as a bullet list.',
      sourceText: '[paste the notes, transcript, or article here]',
      outputFormat: 'markdown',
      extraInstructions:
        'Return 5-8 bullets, each one clear and self-contained. Facts only from the source.',
    },
  }),
  createPreset('article-rewriter-refresh', {
    id: 'refresh-outdated-article',
    title: 'Refresh outdated article',
    description:
      'Update an aging article with fresher structure, clearer claims, and a cleaner handoff into publishing.',
    recommendedFor: 'Editors and founders refreshing older posts that still have a usable core.',
    featured: true,
    tags: ['editorial', 'refresh', 'seo'],
    values: {
      brief: `Refresh this existing article so it feels current, clearer, and easier to publish. Main refresh goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the current article, section, or excerpt here]',
      context: '[add what feels stale, what changed, and what should stay intact]',
      audience: 'startup founders and operators',
      tone: 'direct, current, editorial',
      outputFormat: 'markdown',
      extraInstructions:
        'Return Markdown with sections named "Revised Draft" and "Change Summary". Keep the strongest original ideas, rewrite weak sections, and make the subheads cleaner.',
    },
  }),
  createPreset('article-rewriter-refresh', {
    id: 'shorten-bloated-draft',
    title: 'Shorten bloated draft',
    description:
      'Compress an overlong draft into a tighter, easier-to-read version without losing the core meaning.',
    recommendedFor: 'Writers cleaning up first drafts before publication or internal review.',
    tags: ['editing', 'shortening', 'clarity'],
    values: {
      brief: `Shorten this draft, remove repetition, and tighten the language. Main edit target: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the bloated draft or long section here]',
      context:
        '[note any sections that must stay, legal/compliance lines, or examples to preserve]',
      audience: 'busy professional readers',
      tone: 'clear, concise, plainspoken',
      outputFormat: 'plain text',
      extraInstructions: 'Cut fluff, tighten transitions, and keep the strongest examples.',
    },
  }),
  createPreset('article-rewriter-refresh', {
    id: 'rewrite-for-founders',
    title: 'Rewrite for founders',
    description:
      'Reframe a piece for a sharper founder-facing audience with stronger signal and less generic copy.',
    recommendedFor: 'Operators adapting general copy into founder-led editorial.',
    tags: ['audience shift', 'founders', 'editorial'],
    values: {
      brief: `Rewrite this article for startup founders and operators. Main audience shift: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the article or draft you want reframed here]',
      context: '[add the founder pain points, product angle, or business context to emphasize]',
      audience: 'startup founders and operators',
      tone: 'direct, confident, founder-led',
      outputFormat: 'markdown',
      extraInstructions:
        'Keep it practical, opinionated where useful, and easy to hand off into publishing.',
    },
  }),
  createPreset('article-rewriter-refresh', {
    id: 'rewrite-for-beginners',
    title: 'Rewrite for beginners',
    description:
      'Simplify a dense article into a beginner-friendly version with clearer explanations and less jargon.',
    recommendedFor: 'Teams adapting expert content for broader audiences or top-of-funnel pages.',
    tags: ['simplify', 'beginners', 'education'],
    values: {
      brief: `Rewrite this article for beginners and remove confusing jargon. Main simplification goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the dense article or technical section here]',
      context: '[mention which concepts need plainer explanations or examples]',
      audience: 'beginner readers with limited background knowledge',
      tone: 'clear, friendly, beginner-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Use simpler language, explain concepts briefly, and keep the structure easy to scan.',
    },
  }),
  createPreset('article-rewriter-refresh', {
    id: 'publish-ready-handoff',
    title: 'Turn draft into publish-ready version',
    description:
      'Take a rough internal draft and turn it into a cleaner editorial handoff with better structure and tone.',
    recommendedFor: 'Content teams cleaning up internal notes before final editing.',
    tags: ['handoff', 'publishing', 'cleanup'],
    values: {
      brief: `Turn this rough draft into a publish-ready editorial version. Main handoff goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the rough draft, notes, or assembled copy here]',
      context: '[add publication context, target channel, and what needs polishing most]',
      audience: 'public-facing editorial readers',
      tone: 'clean, polished, editorial',
      outputFormat: 'outline + draft',
      extraInstructions:
        'Fix structure first, then tighten language, and leave a clean handoff for final review.',
    },
  }),

  createPreset('essay-writer-assistant', {
    id: 'argumentative-essay-draft',
    title: 'Argumentative essay draft',
    description:
      'Build a thesis-led outline and first draft with supporting points and a counterargument.',
    recommendedFor: 'Students and researchers starting from a prompt but needing structure fast.',
    featured: true,
    tags: ['essay', 'argument', 'draft'],
    values: {
      brief: `Turn this topic into an argumentative essay with a clear thesis, supporting points, and a usable first draft. Main essay angle: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the assignment prompt, topic notes, thesis idea, or source notes here]',
      context: '[add word count, citation expectations, or assignment structure requirements]',
      tone: 'clear, structured, academic but readable',
      outputFormat: 'outline + draft',
      extraInstructions:
        'Return Markdown with sections named "Thesis and Direction", "Outline", "Draft", and "Revision Notes". Make the argument progression explicit and keep the draft usable, not inflated.',
    },
  }),
  createPreset('essay-writer-assistant', {
    id: 'outline-from-topic',
    title: 'Outline from topic',
    description: 'Generate a strong outline and argument map before moving into full drafting.',
    recommendedFor: 'Writers who need structure first and prose second.',
    tags: ['outline', 'planning', 'argument map'],
    values: {
      brief: `Build a structured outline and argument map from this topic before writing a full draft. Main planning goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the essay topic, notes, reading list, or thesis idea here]',
      context: '[add the assignment type, expected structure, or instructor requirements]',
      tone: 'clear, structured, thoughtful',
      outputFormat: 'outline + draft',
      extraInstructions:
        'Prioritize outline quality, supporting logic, and section progression over polished prose.',
    },
  }),
  createPreset('essay-writer-assistant', {
    id: 'reflection-essay',
    title: 'Reflection essay',
    description: 'Create a more reflective essay structure that still stays coherent and readable.',
    recommendedFor:
      'Personal reflection, learning journal, or professional reflection assignments.',
    tags: ['reflection', 'personal writing', 'structure'],
    values: {
      brief: `Build a reflective essay with a coherent structure and a thoughtful first draft. Main reflection goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the reflection prompt, key experiences, notes, or core ideas here]',
      context:
        '[add the course, setting, event, or professional context the reflection should reference]',
      tone: 'thoughtful, personal, clear',
      outputFormat: 'markdown',
      extraInstructions: 'Keep the tone reflective, but still structured and coherent.',
    },
  }),
  createPreset('essay-writer-assistant', {
    id: 'compare-two-positions',
    title: 'Compare two positions',
    description:
      'Frame an essay around comparison, contrast, and evaluation of two ideas or arguments.',
    recommendedFor: 'Assignments built around contrast, critique, or comparative reasoning.',
    tags: ['compare', 'analysis', 'critical thinking'],
    values: {
      brief: `Create a compare-and-contrast essay structure with a clear evaluation of both positions. Main comparison task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the two positions, readings, notes, or source material here]',
      context: '[add whether the essay should stay neutral, take a side, or synthesize both views]',
      tone: 'analytical, balanced, structured',
      outputFormat: 'arguments + draft',
      extraInstructions:
        'Make the contrasts explicit and avoid repeating the same points in different words.',
    },
  }),
  createPreset('essay-writer-assistant', {
    id: 'revision-plan-from-rough-draft',
    title: 'Revision plan from rough draft',
    description:
      'Turn a messy draft into a clearer outline, stronger structure, and a focused revision checklist.',
    recommendedFor: 'Writers who already have a rough essay but need a better revision path.',
    tags: ['revision', 'editing', 'draft cleanup'],
    values: {
      brief: `Review this rough essay draft, strengthen the structure, and produce a revision-first rewrite plan. Main revision goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the rough essay draft or draft sections here]',
      context:
        '[add the original assignment prompt, grading criteria, or review comments if available]',
      tone: 'clear, structured, constructive',
      outputFormat: 'outline + draft',
      extraInstructions:
        'Identify the weakest sections, suggest a cleaner structure, and leave an actionable revision checklist.',
    },
  }),

  createPreset('tone-voice-rewriter', {
    id: 'founder-voice-rewrite',
    title: 'Founder voice rewrite',
    description:
      'Make the copy sharper, more opinionated, and more founder-led without losing the original meaning.',
    recommendedFor: 'Founders and marketers adapting bland copy into a stronger operator voice.',
    featured: true,
    tags: ['voice', 'founders', 'copywriting'],
    values: {
      brief: `Rewrite this copy in a stronger founder voice while keeping the meaning intact. Main voice goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the source copy, landing page section, or email draft here]',
      context: '[add brand notes, phrases to avoid, or examples of the target founder voice]',
      tone: 'direct, confident, founder-led',
      outputFormat: 'rewritten copy',
      extraInstructions:
        'Return Markdown with sections named "Rewritten Copy" and "What Changed". Make it sharper, cleaner, and less generic without sounding forced.',
    },
  }),
  createPreset('tone-voice-rewriter', {
    id: 'brand-voice-cleanup',
    title: 'Brand voice cleanup',
    description: 'Polish the copy into a more consistent and deliberate brand voice.',
    recommendedFor: 'Teams that already have source copy but need cleaner brand alignment.',
    tags: ['brand', 'cleanup', 'consistency'],
    values: {
      brief: `Rewrite this copy into a cleaner and more consistent brand voice. Main cleanup goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the draft copy, web section, or campaign copy here]',
      context:
        '[add brand style notes, forbidden phrases, or references for how the copy should sound]',
      tone: 'clear, polished, brand-consistent',
      outputFormat: 'multiple voice variants',
      extraInstructions:
        'Show one safe version and one bolder version while keeping the message aligned.',
    },
  }),
  createPreset('tone-voice-rewriter', {
    id: 'expert-tone-rewrite',
    title: 'Expert tone rewrite',
    description: 'Shift the copy into a more credible expert tone without overcomplicating it.',
    recommendedFor: 'Consultants, operators, and B2B teams publishing expertise-led copy.',
    tags: ['expert tone', 'authority', 'b2b'],
    values: {
      brief: `Rewrite this text in a more credible expert tone while staying readable. Main authority goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the source copy or explanation here]',
      context:
        '[add domain context, target audience expertise level, and what should sound more authoritative]',
      tone: 'authoritative, practical, expert-led',
      outputFormat: 'rewritten copy',
      extraInstructions:
        'Keep it specific and credible, but avoid inflated claims or academic clutter.',
    },
  }),
  createPreset('tone-voice-rewriter', {
    id: 'beginner-friendly-rewrite',
    title: 'Beginner-friendly rewrite',
    description:
      'Rewrite dense or intimidating copy into a more approachable version for less experienced readers.',
    recommendedFor: 'Education, onboarding, and top-of-funnel content surfaces.',
    tags: ['beginners', 'clarity', 'education'],
    values: {
      brief: `Rewrite this copy so it feels beginner-friendly and easier to understand. Main simplification goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the technical or intimidating copy here]',
      context:
        '[add what the reader already knows, what confuses them, or what should be explained more gently]',
      tone: 'friendly, clear, reassuring',
      outputFormat: 'rewritten copy',
      extraInstructions:
        'Reduce jargon, explain concepts briefly, and keep the copy warm but still useful.',
    },
  }),
  createPreset('tone-voice-rewriter', {
    id: 'two-voice-variants',
    title: 'Two voice variants',
    description:
      'Generate two distinct rewrites so a team can choose between safer and bolder directions.',
    recommendedFor: 'Brand teams testing how far they want to push a voice shift.',
    tags: ['variants', 'brand testing', 'messaging'],
    values: {
      brief: `Create two voice variants for this copy so the team can compare directions. Main comparison goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the source copy you want rewritten here]',
      context:
        '[add the two voice directions to compare, such as safer vs bolder or expert vs approachable]',
      tone: 'clear, deliberate, contrast-ready',
      outputFormat: 'multiple voice variants',
      extraInstructions: 'Make the differences obvious, but keep both versions publishable.',
    },
  }),

  createPreset('social-post-studio', {
    id: 'launch-post-pack',
    title: 'Launch post pack',
    description: 'Generate a coordinated post pack for a product launch across several channels.',
    recommendedFor: 'Teams announcing launches and needing ready-to-publish social variants fast.',
    featured: true,
    tags: ['launch', 'social', 'distribution'],
    values: {
      brief: `Turn this launch source into a coordinated post pack for social channels. Main launch task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the launch note, announcement draft, or source update here]',
      context: '[add channel priorities, CTA, timing, and what the audience should do next]',
      audience: 'startup founders, operators, and marketers',
      tone: 'confident, concise, launch-ready',
      outputFormat: 'platform post pack',
      extraInstructions:
        'Return separate Markdown sections for LinkedIn, X, Instagram, Threads, and Facebook. Create distinct variants for each platform without repeating the same hook everywhere.',
    },
  }),
  createPreset('social-post-studio', {
    id: 'cross-platform-announcement',
    title: 'Cross-platform announcement',
    description:
      'Adapt one announcement into platform-specific versions without rewriting everything manually.',
    recommendedFor:
      'Operators sharing the same update across multiple networks with different norms.',
    tags: ['announcement', 'multi-channel', 'repurposing'],
    values: {
      brief: `Turn this source into a cross-platform announcement pack. Main announcement task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the announcement, article excerpt, release note, or summary here]',
      context:
        '[note which channels matter most and what message must stay consistent across all of them]',
      audience: 'professional audiences following product and company updates',
      tone: 'clear, channel-aware, professional',
      outputFormat: 'platform post pack',
      extraInstructions:
        'Make each version feel native to its platform while preserving the main announcement.',
    },
  }),
  createPreset('social-post-studio', {
    id: 'thought-leadership-post-set',
    title: 'Thought-leadership post set',
    description: 'Turn one idea into several opinion-led posts for professional social channels.',
    recommendedFor: 'Founders and experts building authority through recurring posts.',
    tags: ['thought leadership', 'linkedin', 'x'],
    values: {
      brief: `Turn this idea into a thought-leadership post set for professional social channels. Main thought-leadership angle: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the article, notes, hot take, or supporting material here]',
      context: '[add the key argument, audience pain point, and CTA if there is one]',
      audience: 'founders, operators, and professional peers',
      tone: 'clear, opinionated, useful',
      outputFormat: 'platform post pack',
      extraInstructions:
        'Make the hooks sharper, avoid vague inspiration, and keep the posts actionable.',
    },
  }),
  createPreset('social-post-studio', {
    id: 'event-promo-post-set',
    title: 'Event promo post set',
    description:
      'Generate event promotion copy for multiple channels with clearer CTAs and deadlines.',
    recommendedFor: 'Webinars, workshops, launches, and event-driven campaigns.',
    tags: ['event', 'promotion', 'cta'],
    values: {
      brief: `Create an event promotion post set across the main social channels. Main event task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the event details, agenda, audience, and signup information here]',
      context: '[add the event date, urgency, registration CTA, and the channels to prioritize]',
      audience: 'people likely to register or attend this event',
      tone: 'clear, persuasive, deadline-aware',
      outputFormat: 'platform post pack',
      extraInstructions:
        'Keep the CTA obvious, include urgency where relevant, and adapt the structure by channel.',
    },
  }),
  createPreset('social-post-studio', {
    id: 'feature-update-pack',
    title: 'Feature update pack',
    description: 'Turn a product update into a channel-ready set of short posts and explanations.',
    recommendedFor: 'Product and growth teams communicating feature updates or shipping notes.',
    tags: ['product update', 'feature', 'social'],
    values: {
      brief: `Turn this product or feature update into a social post pack. Main update task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste the feature note, release update, changelog summary, or product context here]',
      context:
        '[add the target channels, audience segment, and what benefit should lead the message]',
      audience: 'existing users and prospective users following product updates',
      tone: 'clear, practical, product-led',
      outputFormat: 'short captions',
      extraInstructions:
        'Lead with the user benefit, keep jargon low, and make the update easy to scan.',
    },
  }),

  createPreset('content-repurposing-studio', {
    id: 'article-to-social-pack',
    title: 'Article to social pack',
    description:
      'Turn one article into a summary, social posts, hooks, and short distribution assets.',
    recommendedFor: 'Editorial and growth teams extending the life of published content.',
    featured: true,
    tags: ['repurposing', 'social', 'distribution'],
    values: {
      brief: `Turn this article into a reusable social distribution pack. Main repurposing goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the article, draft, or long-form source here]',
      context:
        '[add which outputs matter most, such as LinkedIn post, X thread, summary, or hooks]',
      audience: 'founders, operators, marketers, and newsletter readers',
      tone: 'clear, adaptable, channel-aware',
      outputFormat: 'content pack',
      extraInstructions:
        'Return Markdown sections named "Summary", "LinkedIn Post", "X Thread", "Email Intro", and "Hook Bank". Avoid repeating the same opening line across the assets.',
    },
  }),
  createPreset('content-repurposing-studio', {
    id: 'blog-to-newsletter-and-thread',
    title: 'Blog post to newsletter + thread',
    description: 'Extract a concise newsletter intro and a thread from one long-form source.',
    recommendedFor: 'Teams repurposing written content for owned and social channels together.',
    tags: ['newsletter', 'thread', 'blog'],
    values: {
      brief: `Repurpose this blog post into a newsletter intro and a strong social thread. Main repurposing task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the blog post, article draft, or long-form source here]',
      context: '[add newsletter tone, thread audience, CTA, and any sections to emphasize]',
      audience: 'newsletter readers and social followers interested in practical takeaways',
      tone: 'clear, concise, channel-aware',
      outputFormat: 'summary + posts + email',
      extraInstructions:
        'Keep the newsletter intro concise, make the thread punchy, and avoid repeating the article lede word-for-word.',
    },
  }),
  createPreset('content-repurposing-studio', {
    id: 'transcript-to-content-pack',
    title: 'Transcript to content pack',
    description: 'Turn a raw transcript or interview excerpt into cleaner publishing assets.',
    recommendedFor: 'Podcast, webinar, interview, and video content repurposing.',
    tags: ['transcript', 'video', 'content ops'],
    values: {
      brief: `Turn this transcript into a clean content pack with summary, social assets, and short hooks. Main transcript task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the transcript excerpt, interview notes, or spoken-content source here]',
      context:
        '[add the target channels, key themes to surface, and what should be trimmed or ignored]',
      audience: 'people who want the key insights without reading the full transcript',
      tone: 'clear, distilled, channel-aware',
      outputFormat: 'content pack',
      extraInstructions:
        'Distill the strongest ideas, remove transcript noise, and keep the outputs publication-ready.',
    },
  }),
  createPreset('content-repurposing-studio', {
    id: 'notes-to-distribution-pack',
    title: 'Notes to distribution pack',
    description:
      'Turn rough notes into a tighter short-form pack without rebuilding the message from zero.',
    recommendedFor:
      'Founders and operators who work from rough internal notes rather than clean drafts.',
    tags: ['notes', 'launch', 'distribution'],
    values: {
      brief: `Turn these rough notes into a distribution-ready content pack. Main distribution task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the notes, bullets, or rough internal copy here]',
      context: '[add the launch context, channels, and what output mix matters most]',
      audience: 'target customers and followers who need a concise version of the update',
      tone: 'clear, adaptable, launch-ready',
      outputFormat: 'summary + posts + email',
      extraInstructions:
        'Clean up the source heavily, but keep the intended message and produce assets that are easy to publish fast.',
    },
  }),
  createPreset('content-repurposing-studio', {
    id: 'case-study-to-short-form-assets',
    title: 'Case study to short-form assets',
    description:
      'Turn one case study into short posts, hooks, and summary assets for distribution.',
    recommendedFor:
      'Agencies and product teams promoting wins without republishing the whole case study everywhere.',
    tags: ['case study', 'short-form', 'growth'],
    values: {
      brief: `Turn this case study into short-form distribution assets. Main case-study task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the case study, outcome summary, or customer story here]',
      context:
        '[add the audience, channels, and whether to emphasize proof, process, or business outcome]',
      audience: 'prospects comparing solutions and looking for credible proof points',
      tone: 'clear, credible, benefit-led',
      outputFormat: 'content pack',
      extraInstructions:
        'Lead with outcomes, make the hooks specific, and avoid fluffy success-story language.',
    },
  }),

  createPreset('product-to-social-campaign', {
    id: 'new-product-launch-campaign',
    title: 'New product launch campaign',
    description:
      'Build a launch-ready campaign pack from product facts, benefits, and announcement context.',
    recommendedFor:
      'Ecommerce and product teams preparing a new product launch across social channels.',
    featured: true,
    tags: ['launch', 'campaign', 'product marketing'],
    values: {
      brief: `Turn this product information into a new product launch campaign pack. Main launch task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste product facts, benefits, launch notes, offer details, and existing copy here]',
      context:
        '[add launch timing, CTA, discount details if relevant, and what angle should lead the campaign]',
      audience: 'shoppers comparing options on social and marketplace channels',
      tone: 'clear, persuasive, campaign-ready',
      outputFormat: 'campaign pack',
      extraInstructions:
        'Return Markdown sections named "Main Campaign Post", "Supporting Posts", "Hook Variants", and "CTA Variants". Keep the angles varied and the offer clear.',
    },
  }),
  createPreset('product-to-social-campaign', {
    id: 'sale-promo-week-campaign',
    title: 'Sale / promo week campaign',
    description:
      'Generate a tighter campaign pack around urgency, discount messaging, and timed CTAs.',
    recommendedFor: 'Promo windows, sale periods, and discount-led campaigns.',
    tags: ['sale', 'promo', 'urgency'],
    values: {
      brief: `Turn this product and offer information into a promo-week campaign pack. Main promo task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product details, pricing, offer, and sale context here]',
      context: '[add promo timing, discount terms, target channels, and urgency constraints]',
      audience: 'shoppers considering a time-sensitive offer',
      tone: 'clear, persuasive, urgency-aware',
      outputFormat: 'posts + hooks + ctas',
      extraInstructions:
        'Keep hooks short, make CTAs specific, and create urgency without sounding spammy.',
    },
  }),
  createPreset('product-to-social-campaign', {
    id: 'feature-spotlight-campaign',
    title: 'Feature spotlight campaign',
    description: 'Build a campaign around one product feature or benefit instead of a full launch.',
    recommendedFor: 'Brands promoting one differentiator or key use case.',
    tags: ['feature', 'benefit-led', 'campaign'],
    values: {
      brief: `Build a feature spotlight campaign around this product. Main spotlight task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste the product details and the feature or benefit you want to spotlight here]',
      context: '[add the audience segment, social channels, and why this feature matters now]',
      audience: 'prospects who need a clearer reason to care about one specific feature',
      tone: 'clear, benefit-led, persuasive',
      outputFormat: 'campaign pack',
      extraInstructions:
        'Keep the pack centered on one strong differentiator and vary the hooks around that idea.',
    },
  }),
  createPreset('product-to-social-campaign', {
    id: 'ugc-style-social-pack',
    title: 'UGC-style social pack',
    description:
      'Create a more casual, use-case-led campaign pack that feels closer to organic social.',
    recommendedFor: 'Brands testing a lighter, more social-native tone around a product.',
    tags: ['ugc', 'social-native', 'casual'],
    values: {
      brief: `Turn this product information into a more social-native campaign pack. Main UGC-style task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product info, use cases, key benefits, and any source copy here]',
      context:
        '[add the platform focus, creator-style constraints, and what should feel more organic or casual]',
      audience: 'social users discovering products through lighter, creator-style content',
      tone: 'casual, clear, social-native',
      outputFormat: 'posts + hooks + ctas',
      extraInstructions:
        'Keep the copy usable on social, avoid corporate stiffness, and make the scenarios feel real.',
    },
  }),
  createPreset('product-to-social-campaign', {
    id: 'seasonal-campaign-pack',
    title: 'Seasonal campaign pack',
    description: 'Wrap product messaging around a seasonal moment, event, or holiday angle.',
    recommendedFor: 'Seasonal promotions, holiday pushes, and time-specific merchandising.',
    tags: ['seasonal', 'holiday', 'campaign'],
    values: {
      brief: `Turn this product information into a seasonal campaign pack. Main seasonal task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste product facts, offer details, and any existing seasonal copy here]',
      context: '[add the seasonal moment, event, audience, and campaign timing]',
      audience: 'shoppers responding to a seasonal moment or themed offer',
      tone: 'clear, timely, persuasive',
      outputFormat: 'campaign pack',
      extraInstructions:
        'Tie the product naturally to the seasonal angle and keep the CTAs specific to the campaign window.',
    },
  }),

  createPreset('product-listing-writer', {
    id: 'shopify-listing-pack',
    title: 'Shopify listing pack',
    description: 'Generate a clean title, bullets, and description for a Shopify product page.',
    recommendedFor: 'Shopify operators building or refreshing standard storefront listings.',
    featured: true,
    tags: ['shopify', 'listing', 'ecommerce'],
    values: {
      brief: `Write a conversion-focused Shopify listing pack from this product information. Main listing task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste product name, features, specs, benefits, audience notes, and current copy here]',
      context:
        '[add storefront positioning, competitor context, and any claims or terms that must stay compliant]',
      audience: 'online shoppers comparing similar products',
      tone: 'clear, benefit-led, conversion-focused',
      outputFormat: 'listing pack',
      extraInstructions:
        'Return Markdown sections named "Product Title", "Bullet Points", and "Product Description". Generate a stronger title, five bullets, and a concise description that is easy to publish.',
    },
  }),
  createPreset('product-listing-writer', {
    id: 'amazon-style-listing',
    title: 'Amazon-style listing',
    description: 'Generate tighter marketplace copy with scannable bullets and less fluff.',
    recommendedFor: 'Marketplace operators who need compact, structured listing copy.',
    tags: ['marketplace', 'amazon-style', 'structured'],
    values: {
      brief: `Turn this product information into a tighter marketplace listing. Main marketplace task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste product details, features, specs, and any existing listing copy here]',
      context: '[add marketplace rules, character constraints, and differentiators to emphasize]',
      audience: 'marketplace shoppers scanning several options quickly',
      tone: 'clear, concise, benefit-led',
      outputFormat: 'title + bullets + description',
      extraInstructions:
        'Keep every bullet scannable, avoid hype, and prioritize clarity over flourish.',
    },
  }),
  createPreset('product-listing-writer', {
    id: 'marketplace-compact-copy',
    title: 'Marketplace compact copy',
    description:
      'Produce an even more compressed listing package for space-constrained product pages.',
    recommendedFor: 'Catalog teams needing cleaner compact copy across many listings.',
    tags: ['compact', 'catalog', 'marketplace'],
    values: {
      brief: `Write a compact listing package from this product data. Main compact-copy goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product facts, attributes, and source copy here]',
      context:
        '[add length constraints, marketplace type, and what objections or questions matter most]',
      audience: 'shoppers skimming product cards and listing summaries',
      tone: 'compact, clear, conversion-focused',
      outputFormat: 'title + bullets + description',
      extraInstructions:
        'Keep the title lean, make the bullets fast to scan, and avoid long descriptive paragraphs.',
    },
  }),
  createPreset('product-listing-writer', {
    id: 'benefit-led-premium-listing',
    title: 'Benefit-led premium listing',
    description:
      'Reframe the product around premium benefits and stronger positioning rather than feature dumping.',
    recommendedFor: 'Products that need a more premium or differentiated presentation.',
    tags: ['premium', 'benefits', 'positioning'],
    values: {
      brief: `Write a more premium, benefit-led listing from this product information. Main premium-positioning task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product details, benefits, quality cues, and source copy here]',
      context:
        '[add the positioning angle, target customer, and what should feel more elevated or differentiated]',
      audience: 'buyers willing to pay for stronger perceived quality or fit',
      tone: 'clear, polished, premium but credible',
      outputFormat: 'listing pack',
      extraInstructions:
        'Lead with benefits and positioning, but keep the claims believable and easy to publish.',
    },
  }),
  createPreset('product-listing-writer', {
    id: 'rewrite-weak-listing',
    title: 'Rewrite weak listing',
    description:
      'Take an underperforming or bland listing and turn it into a clearer, more structured version.',
    recommendedFor: 'Catalog cleanup and conversion-oriented rewrites of existing listings.',
    tags: ['rewrite', 'cleanup', 'conversion'],
    values: {
      brief: `Rewrite this weak or bland listing into a clearer and more conversion-focused version. Main rewrite goal: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the current listing copy and product facts here]',
      context:
        '[add what feels weak, what competitors do better, and what the revised listing should emphasize]',
      audience: 'shoppers who need clearer reasons to choose this product',
      tone: 'clear, structured, conversion-focused',
      outputFormat: 'listing pack',
      extraInstructions:
        'Fix the structure first, remove generic phrasing, and make the value proposition easier to understand.',
    },
  }),

  createPreset('product-card-autofill', {
    id: 'fill-missing-marketplace-fields',
    title: 'Fill missing marketplace fields',
    description:
      'Complete structured marketplace fields from partial product data and operational notes.',
    recommendedFor: 'Catalog operators filling incomplete marketplace records.',
    featured: true,
    tags: ['marketplace', 'structured fields', 'ops'],
    values: {
      brief: `Fill the missing marketplace product fields from the available product data. Main field-completion task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the known product attributes, product facts, and incomplete fields here]',
      context:
        '[add marketplace type, required fields, formatting rules, and any data quality caveats]',
      audience: 'catalog and marketplace operations teams',
      tone: 'structured, concise, operations-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Return clean field-ready content and note where the available data is too weak to infer a field safely.',
    },
  }),
  createPreset('product-card-autofill', {
    id: 'normalize-messy-product-attributes',
    title: 'Normalize messy product attributes',
    description:
      'Clean up inconsistent product attributes into a more standardized card-ready shape.',
    recommendedFor: 'Teams dealing with vendor feeds, inconsistent naming, or messy raw data.',
    tags: ['normalization', 'catalog', 'ops cleanup'],
    values: {
      brief: `Normalize these messy product attributes into a cleaner structured product card. Main normalization task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste the messy attributes, raw feed data, or inconsistent product details here]',
      context:
        '[add the target card structure, naming conventions, and any fields that must be normalized first]',
      audience: 'operations teams preparing cleaner product records',
      tone: 'structured, precise, normalization-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Standardize wording, resolve obvious inconsistencies, and keep the output field-oriented.',
    },
  }),
  createPreset('product-card-autofill', {
    id: 'generate-short-attribute-summaries',
    title: 'Generate short attribute summaries',
    description: 'Turn raw product data into short structured attribute summaries for card use.',
    recommendedFor:
      'Storefronts and catalogs that need compact attribute text rather than long prose.',
    tags: ['attributes', 'summaries', 'compact'],
    values: {
      brief: `Generate short structured attribute summaries from this product data. Main summary task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product attributes, specs, and raw notes here]',
      context:
        '[add the target storefront or marketplace card format and the fields that need compact text]',
      audience: 'teams building compact product cards and comparison surfaces',
      tone: 'concise, structured, ops-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Keep the summaries short, consistent, and ready to slot into structured fields.',
    },
  }),
  createPreset('product-card-autofill', {
    id: 'storefront-card-completion',
    title: 'Storefront card completion',
    description:
      'Fill missing card fields for storefront use where the product page needs stronger structured support.',
    recommendedFor: 'Direct-to-consumer storefronts with incomplete card-level product content.',
    tags: ['storefront', 'structured content', 'card completion'],
    values: {
      brief: `Complete this storefront product card from the available product information. Main storefront-card task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste the current product data, existing card fields, and missing field list here]',
      context:
        '[add the storefront context, card layout constraints, and what information should be prioritized]',
      audience: 'storefront teams improving structured product presentation',
      tone: 'clear, structured, practical',
      outputFormat: 'markdown',
      extraInstructions:
        'Prioritize clean structured fields over long persuasive prose and note if any fields are too under-specified.',
    },
  }),
  createPreset('product-card-autofill', {
    id: 'ops-handoff-structured-card',
    title: 'Ops handoff structured card',
    description:
      'Prepare a structured product card for operations handoff, cleanup, or later API enrichment.',
    recommendedFor:
      'Teams moving between manual enrichment now and more structured workflows later.',
    tags: ['handoff', 'ops', 'future api'],
    values: {
      brief: `Prepare an operations-ready structured product card from this source data. Main handoff task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the available product data, notes, and missing field information here]',
      context:
        '[add the downstream use case, such as ops handoff, feed cleanup, or future API enrichment]',
      audience: 'operations teams preparing product records for downstream workflows',
      tone: 'structured, explicit, handoff-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Make the output easy to hand off, clearly separated by field, and explicit about any uncertain data.',
    },
  }),

  createPreset('seo-brief-to-draft', {
    id: 'keyword-to-brief',
    title: 'Keyword to brief',
    description: 'Generate a planning-first SEO brief with intent, headings, and coverage notes.',
    recommendedFor: 'SEO and editorial teams planning a page before writing it.',
    featured: true,
    tags: ['seo', 'brief', 'planning'],
    values: {
      brief: `Turn this keyword or topic into a practical SEO brief. Main SEO planning task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the keyword, topic notes, competing angles, or product context here]',
      context: '[add SERP angle, funnel stage, internal linking intent, and editorial direction]',
      audience: 'search-driven readers comparing options and looking for practical answers',
      tone: 'clear, authoritative, search-focused',
      outputFormat: 'seo brief + draft',
      extraInstructions:
        'Return Markdown sections named "Search Intent", "Outline", "Coverage Notes", and "Working Draft". Prioritize concrete coverage and heading decisions before the draft gets too long.',
    },
  }),
  createPreset('seo-brief-to-draft', {
    id: 'keyword-to-publishable-first-draft',
    title: 'Keyword to publishable first draft',
    description: 'Move from one keyword or topic into a structured first draft, not only a brief.',
    recommendedFor: 'Teams that want a usable first pass after planning, not just outline notes.',
    tags: ['seo draft', 'article', 'content ops'],
    values: {
      brief: `Turn this keyword or topic into a practical SEO brief and then a publishable first draft. Main drafting task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the keyword, notes, source material, and supporting context here]',
      context:
        '[add target angle, product context, funnel stage, and what should be covered or avoided]',
      audience: 'search-driven readers expecting practical, scannable answers',
      tone: 'clear, authoritative, search-focused',
      outputFormat: 'outline + notes + draft',
      extraInstructions:
        'Keep headings search-friendly, make the coverage concrete, and leave a draft that is easy to refine.',
    },
  }),
  createPreset('seo-brief-to-draft', {
    id: 'topic-cluster-article-draft',
    title: 'Topic cluster article draft',
    description:
      'Create a cluster-friendly article brief and draft around a broader topic, not just one keyword.',
    recommendedFor: 'Editorial teams building topic clusters and supporting content hubs.',
    tags: ['topic cluster', 'content strategy', 'seo'],
    values: {
      brief: `Turn this topic into a cluster-friendly SEO brief and article draft. Main cluster task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the topic, subtopics, internal notes, and related pages here]',
      context:
        '[add the cluster context, supporting pages, internal linking priorities, and target reader intent]',
      audience: 'readers exploring a broader topic and related subtopics',
      tone: 'clear, structured, search-focused',
      outputFormat: 'seo brief + draft',
      extraInstructions:
        'Show how the article fits into the wider topic cluster and keep the headings aligned to that role.',
    },
  }),
  createPreset('seo-brief-to-draft', {
    id: 'bottom-funnel-comparison-draft',
    title: 'Bottom-funnel comparison draft',
    description: 'Frame a draft for readers comparing options and closer to decision-making.',
    recommendedFor: 'Commercial or product-led SEO pages aimed at comparison and consideration.',
    tags: ['comparison', 'bottom funnel', 'commercial seo'],
    values: {
      brief: `Create a bottom-funnel SEO brief and comparison-style first draft. Main comparison task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the keyword, product context, alternatives, and commercial notes here]',
      context:
        '[add the comparison angle, target reader decision stage, and what conversion or CTA should matter later]',
      audience: 'readers comparing solutions and moving toward a decision',
      tone: 'clear, practical, commercial but credible',
      outputFormat: 'outline + notes + draft',
      extraInstructions:
        'Make the comparison intent obvious, keep the structure useful, and avoid sounding like pure sales copy.',
    },
  }),
  createPreset('seo-brief-to-draft', {
    id: 'informational-query-brief',
    title: 'Informational query brief',
    description: 'Build a clearer plan and draft for a primarily informational query or question.',
    recommendedFor: 'Top-of-funnel educational content and answer-style pages.',
    tags: ['informational', 'top funnel', 'answer content'],
    values: {
      brief: `Create an informational SEO brief and first draft for this query. Main informational task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the question, keyword, notes, or existing outline here]',
      context:
        '[add what the reader wants answered, what misconceptions to avoid, and any supporting product context]',
      audience: 'readers looking for straightforward informational answers',
      tone: 'clear, helpful, search-focused',
      outputFormat: 'seo brief + draft',
      extraInstructions:
        'Prioritize clarity, answer the query directly, and keep the outline easy to expand later.',
    },
  }),

  createPreset('seo-refresh-optimizer', {
    id: 'refresh-declining-article',
    title: 'Refresh declining article',
    description:
      'Diagnose a weakening article and produce a clearer refresh plan with rewritten sections.',
    recommendedFor: 'Teams improving older pages that still matter but feel stale or underpowered.',
    featured: true,
    tags: ['refresh', 'seo', 'diagnosis'],
    values: {
      brief: `Review this existing article and produce a refresh plan plus rewritten sections. Main refresh task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the current article, page copy, or excerpt here]',
      context:
        '[add what feels weak, where traffic dropped, and what should improve after the refresh]',
      audience: 'search-driven readers expecting current and practical answers',
      tone: 'clear, diagnostic, search-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Identify stale sections, weak angles, and structural gaps before rewriting the most important parts.',
    },
  }),
  createPreset('seo-refresh-optimizer', {
    id: 'improve-ai-answer-readiness',
    title: 'Improve AI-answer readiness',
    description:
      'Make an existing page easier for answer systems to understand, summarize, and cite.',
    recommendedFor: 'Teams optimizing current pages for modern answer-style discovery.',
    tags: ['ai answers', 'seo refresh', 'content structure'],
    values: {
      brief: `Review this page for AI-answer readiness and produce a clearer refresh plan. Main answer-readiness task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the current page copy or article excerpt here]',
      context:
        '[add the target topic, search intent, and where the page currently feels hard to extract or summarize]',
      audience: 'readers and systems looking for clear, direct answers',
      tone: 'clear, structured, answer-friendly',
      outputFormat: 'markdown',
      extraInstructions:
        'Prioritize direct-answer structure, scannable sections, and cleaner metadata/heading suggestions.',
    },
  }),
  createPreset('seo-refresh-optimizer', {
    id: 'rewrite-stale-sections',
    title: 'Rewrite stale sections',
    description:
      'Focus on rewriting the weakest or oldest sections instead of rebuilding the entire page.',
    recommendedFor: 'Pages where only parts of the copy need a serious refresh.',
    tags: ['section rewrite', 'refresh', 'editing'],
    values: {
      brief: `Inspect this page and rewrite only the stale or weak sections. Main section-refresh task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the existing page or the sections that need attention here]',
      context: '[note which sections feel stale, repetitive, thin, or structurally weak]',
      audience: 'readers who need clearer and more current coverage',
      tone: 'clear, practical, editorial',
      outputFormat: 'markdown',
      extraInstructions:
        'Leave the stronger sections alone and concentrate the rewrite on the weakest parts.',
    },
  }),
  createPreset('seo-refresh-optimizer', {
    id: 'metadata-headings-cleanup',
    title: 'Metadata + headings cleanup',
    description:
      'Prioritize title, meta description, heading structure, and packaging quality around an existing page.',
    recommendedFor: 'Pages where the core content is acceptable but packaging is weak.',
    tags: ['metadata', 'headings', 'packaging'],
    values: {
      brief: `Audit this page for metadata and heading cleanup opportunities. Main packaging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the page copy, title, headings, and metadata if available here]',
      context:
        '[add the target query, page purpose, and what parts of the packaging already feel off]',
      audience: 'search readers choosing whether this page is worth opening',
      tone: 'clear, diagnostic, search-aware',
      outputFormat: 'markdown',
      extraInstructions:
        'Focus on title, meta description, heading hierarchy, and section labels before deeper prose rewrites.',
    },
  }),
  createPreset('seo-refresh-optimizer', {
    id: 'thin-page-expansion',
    title: 'Thin page expansion',
    description:
      'Expand an underdeveloped page into a fuller version with stronger coverage and fewer obvious gaps.',
    recommendedFor: 'Thin pages that need deeper coverage rather than only line edits.',
    tags: ['thin content', 'expansion', 'coverage'],
    values: {
      brief: `Review this thin page and produce a stronger expansion plan with improved sections. Main expansion task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the thin page, short article, or underdeveloped copy here]',
      context:
        '[add what coverage is missing, which questions readers still have, and where the page feels incomplete]',
      audience: 'readers needing fuller practical coverage before they move on',
      tone: 'clear, complete, search-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Identify the biggest content gaps, then expand the page in a way that still stays scannable.',
    },
  }),

  createPreset('metadata-pack-generator', {
    id: 'editorial-page-metadata-pack',
    title: 'Editorial page metadata pack',
    description: 'Generate a packaging layer for an article or editorial page from existing copy.',
    recommendedFor:
      'Editors who already have the page copy but need faster metadata and packaging support.',
    featured: true,
    tags: ['metadata', 'editorial', 'publishing'],
    values: {
      brief: `Create a metadata pack for this editorial page or article. Main packaging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the article draft, page copy, or editorial source here]',
      context:
        '[add the target query, audience, and any tone or channel requirements for metadata]',
      audience: 'search and social users deciding whether to open the page',
      tone: 'clear, concise, packaging-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Generate title, meta description, OG copy, FAQ ideas, and schema hints that fit the page.',
    },
  }),
  createPreset('metadata-pack-generator', {
    id: 'product-page-metadata-pack',
    title: 'Product page metadata pack',
    description: 'Generate metadata and packaging cues for a product or commercial landing page.',
    recommendedFor: 'Ecommerce, product, and growth teams packaging product pages.',
    tags: ['product page', 'metadata', 'commercial'],
    values: {
      brief: `Create a metadata pack for this product page or commercial landing page. Main product-page packaging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product page copy, offer details, or landing page draft here]',
      context:
        '[add the target audience, product angle, and what should be emphasized in search and social packaging]',
      audience: 'buyers evaluating whether to click through to the page',
      tone: 'clear, concise, commercially credible',
      outputFormat: 'markdown',
      extraInstructions:
        'Keep the packaging clear and specific, and make the OG copy suitable for share surfaces.',
    },
  }),
  createPreset('metadata-pack-generator', {
    id: 'launch-page-metadata-pack',
    title: 'Launch page metadata pack',
    description: 'Generate metadata, OG copy, and FAQ ideas for a launch or campaign page.',
    recommendedFor: 'Teams shipping launch pages that need fast packaging before going live.',
    tags: ['launch', 'og copy', 'faq'],
    values: {
      brief: `Create a metadata pack for this launch or campaign page. Main launch-page packaging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the launch page copy, campaign summary, or announcement draft here]',
      context: '[add the campaign timing, CTA, audience, and what angle should lead metadata]',
      audience: 'prospects encountering the launch in search and social contexts',
      tone: 'clear, timely, launch-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Make the title and meta description strong enough for launch traffic and propose FAQ ideas that support the page.',
    },
  }),
  createPreset('metadata-pack-generator', {
    id: 'faq-schema-ideas',
    title: 'FAQ + schema ideas',
    description:
      'Focus on FAQ questions, structured data hints, and packaging support around an existing page.',
    recommendedFor:
      'Pages that already have acceptable copy but need better support around FAQs and schema.',
    tags: ['faq', 'schema', 'support'],
    values: {
      brief: `Generate FAQ ideas and schema hints for this page. Main FAQ/schema task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the page copy, article draft, or product page text here]',
      context:
        '[add the page purpose, likely user questions, and what structured data seems relevant]',
      audience: 'readers and systems looking for clearer page packaging and supporting answers',
      tone: 'clear, concise, support-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Prioritize FAQ ideas that genuinely support the page and keep schema hints practical rather than exhaustive.',
    },
  }),
  createPreset('metadata-pack-generator', {
    id: 'social-og-packaging-pack',
    title: 'Social / OG packaging pack',
    description: 'Focus more heavily on social share copy and Open Graph framing around the page.',
    recommendedFor: 'Pages that rely on share surfaces as much as search snippets.',
    tags: ['open graph', 'social', 'metadata'],
    values: {
      brief: `Create a metadata pack with stronger social and OG framing for this page. Main social-packaging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the source page, launch note, or article draft here]',
      context:
        '[add the social channels, audience expectations, and what share angle should feel strongest]',
      audience: 'people discovering the page through shared links and social previews',
      tone: 'clear, punchy, packaging-focused',
      outputFormat: 'markdown',
      extraInstructions:
        'Make OG copy stronger, keep the title and description aligned, and suggest FAQ ideas only if they add real value.',
    },
  }),

  createPreset('app-prompt-pack-builder', {
    id: 'idea-to-app-prompt-pack',
    title: 'Idea to app prompt pack',
    description:
      'Turn one rough product idea into a practical multi-step prompt sequence for AI app builders.',
    recommendedFor:
      'Founders and solo builders starting from a rough idea rather than a complete spec.',
    featured: true,
    tags: ['builder', 'vibe coding', 'prompt pack'],
    values: {
      brief: `Turn this product idea into a structured app-building prompt pack. Main build task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the product idea, user problem, and rough scope here]',
      context: '[add the target builder tool, stack constraints, deadline, or product assumptions]',
      audience:
        'solo builders and operators using AI coding tools to move from idea to first build',
      tone: 'clear, structured, builder-friendly',
      outputFormat: 'markdown',
      extraInstructions:
        'Break the work into a usable sequence of prompts for product framing, UI, backend, integration, QA, and deploy.',
    },
  }),
  createPreset('app-prompt-pack-builder', {
    id: 'prd-to-build-sequence',
    title: 'PRD to build sequence',
    description: 'Translate a rough PRD into a better ordered prompt chain for implementation.',
    recommendedFor:
      'Teams that already have a spec but need a clearer prompt flow for AI build tools.',
    tags: ['prd', 'implementation', 'prompt sequence'],
    values: {
      brief: `Turn this PRD or rough spec into a build-ready prompt sequence. Main PRD translation task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the PRD, rough spec, feature list, or product requirements here]',
      context:
        '[add the target tool, stack, team constraints, and whether the pack should lean product-first or implementation-first]',
      audience:
        'builders using AI tools but wanting stronger sequencing and clearer implementation prompts',
      tone: 'clear, structured, implementation-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Split the sequence into planning, UI, backend, integrations, QA, and deployment prompts.',
    },
  }),
  createPreset('app-prompt-pack-builder', {
    id: 'landing-page-prompt-pack',
    title: 'Landing page prompt pack',
    description:
      'Generate a structured prompt set for building and refining a landing page quickly.',
    recommendedFor: 'Founders and growth operators using AI builders to ship pages fast.',
    tags: ['landing page', 'copy', 'builder'],
    values: {
      brief: `Create a prompt pack for building a landing page with AI tools. Main landing-page task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the offer, audience, product details, and rough messaging here]',
      context: '[add the builder target, page sections needed, visual constraints, and CTA goal]',
      audience: 'builders who need a cleaner sequence for page structure, copy, and delivery',
      tone: 'clear, practical, builder-friendly',
      outputFormat: 'markdown',
      extraInstructions:
        'Include prompts for page structure, hero copy, proof, FAQ, CTAs, and final refinement.',
    },
  }),
  createPreset('app-prompt-pack-builder', {
    id: 'integration-prompt-pack',
    title: 'Integration prompt pack',
    description:
      'Build a prompt sequence around one specific integration such as Stripe, Firebase, or OpenRouter.',
    recommendedFor:
      'Builders who already have the app idea and now need a tighter integration implementation flow.',
    tags: ['integration', 'backend', 'implementation'],
    values: {
      brief: `Create a prompt pack for implementing one product integration. Main integration task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText: '[paste the app context, current stack, and the integration requirements here]',
      context:
        '[add the target integration, authentication model, environment constraints, and what must be implemented safely]',
      audience: 'builders working through one integration step in an AI coding workflow',
      tone: 'clear, structured, implementation-ready',
      outputFormat: 'markdown',
      extraInstructions:
        'Sequence the prompts from architecture and setup through implementation, validation, and cleanup.',
    },
  }),
  createPreset('app-prompt-pack-builder', {
    id: 'bugfix-debug-prompt-chain',
    title: 'Bugfix / debug prompt chain',
    description:
      'Turn an error, failing feature, or broken flow into a more disciplined debugging prompt sequence.',
    recommendedFor: 'Builders using AI coding tools for debugging and iterative fixes.',
    tags: ['debugging', 'bugfix', 'prompt chain'],
    values: {
      brief: `Create a debugging prompt chain for this broken feature or error. Main debugging task: ${AI_WORKFLOW_TASK_STARTER_TOKEN}.`,
      sourceText:
        '[paste the bug description, error logs, failing behavior, and relevant code context here]',
      context: '[add the stack, what changed recently, and what a correct fix should preserve]',
      audience: 'builders who need a stepwise debugging flow instead of one giant rescue prompt',
      tone: 'clear, diagnostic, builder-friendly',
      outputFormat: 'markdown',
      extraInstructions:
        'Sequence the prompts from bug triage through root-cause analysis, fix proposal, verification, and regression checks.',
    },
  }),
];

export function getTaskPresetsForTool(toolSlug: string) {
  return aiWorkflowTaskPresets.filter((preset) => preset.toolSlug === toolSlug);
}

export function getTaskPresetById(toolSlug: string, presetId: string | null | undefined) {
  if (!presetId) {
    return null;
  }

  return (
    aiWorkflowTaskPresets.find(
      (preset) => preset.toolSlug === toolSlug && preset.id === presetId,
    ) ?? null
  );
}

export function getDefaultTaskPresetForTool(toolSlug: string) {
  const presets = getTaskPresetsForTool(toolSlug);
  return presets.find((preset) => preset.featured) ?? presets[0] ?? null;
}

export function getTaskPresetFieldLabels(values: AiWorkflowTaskPresetValues) {
  return (Object.keys(values) as AiWorkflowTaskPresetFieldKey[])
    .filter((key) => {
      const value = values[key];
      return typeof value === 'string' && value.trim().length > 0;
    })
    .map((key) => aiWorkflowTaskPresetFieldLabels[key]);
}
